import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Grid from '@mui/material/Grid';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { fromInvoices, useAppDispatch } from '../../store';
import { fromOrder } from '../../store/rootReducer';
import Forbidden from '../forbidden';
import OrderTable from './OrderTable';
import { useQuery } from '../../routes';
import ButtonToSubscriptionManagement from './ButtonToSubscriptionManagement';
import ButtonToLicenseManagement from './ButtonToLicenseManagement';
import { LoadingProgress } from '../../layout';
import { getFeatureFromPlan, Invoice, InvoiceState, isTruePlatformFeature, OrderStatus } from '../../models';
import InvoiceTable from './InvoiceTable';
import {
  GET_INVOICE_CANNOT_READ_RECURLY_SUBSCRIPTION,
  GET_INVOICE_RECURLY_SUBSCRIPTION_NOT_EXISTED,
} from '../../store/invoiceSlice';
import ButtonToOrderConfirmation from './ButtonToOrderConfirmation';
import { Panel } from '../../components/common/Panel';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    padding: theme.spacing(0, 4),
    height: '100%',
    overflowY: 'auto',
  },
  title: {
    margin: theme.spacing(2.5, 0, 2, 0),
    display: 'flex',
    fontSize: theme.spacing(3),
  },
  statusContainer: {
    padding: theme.spacing(6, 0),
  },
  statusTitle: {
    fontSize: theme.spacing(2.5),
    marginBottom: theme.spacing(2),
    color: '#172B4D',
    fontWeight: 600,
  },
  statusDescription: {
    marginBottom: theme.spacing(2),
    fontSize: theme.spacing(2),
    fontWeight: '400',
  },
  manageLicenseButton: {
    height: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontWeight: '500',
  },
  tableCell: {
    padding: theme.spacing(1.75, 0),
    maxWidth: theme.spacing(33),
    '&.MuiTableCell-root': {
      paddingBottom: theme.spacing(1),
      borderBottom: '1px solid #DCDFE6',
    },
  },
  tableInnerCell: {
    padding: theme.spacing(2, 0),
    maxWidth: theme.spacing(33),
    color: '#172B4D',
  },
}));

const PaymentStatus = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { get, getAll } = useQuery();
  const orderId = get('orderId');
  const order = useSelector(fromOrder.selectOrderResponse);
  const invoiceNumbers = Array.from(new Set(getAll('invoiceNumber')));
  const [loading, setLoading] = useState(true);
  const invoices = (useSelector(
    fromInvoices.selectInvoiceByNumbers([...invoiceNumbers]),
  ).filter(it => it!!) as Invoice[]);
  const [isForbiddenError, setIsForbiddenError] = useState(false);
  const errors = useSelector(fromInvoices.selectErrors());

  const singleProductInvoices = invoices
    .filter(invoice => invoiceNumbers.includes(invoice?.invoiceNumber!!))
    .sort((a, b) => {
      if (a?.state! > b?.state!) return -1;
      if (a?.state! < b?.state!) return 1;
      return 0;
    });

  const isTrue = order?.orderItems?.some(item => {
    const feature = getFeatureFromPlan(item.planId);
    if (feature) {
      return isTruePlatformFeature(feature);
    }
    return false;
  });

  const failedInvoices: Array<string> = useMemo(() => invoices
    .filter(it => it?.state === InvoiceState.FAILED)
    .map(invoice => invoice.invoiceNumber!!), [invoices]);

  const pendingInvoices: Array<string> = useMemo(() => {
    if (invoices.length === 0) {
      return invoiceNumbers;
    }
    return invoices
      .filter(it => it?.state !== InvoiceState.FAILED && it?.state !== InvoiceState.PAID)
      .map(invoice => invoice.invoiceNumber!!);
  }, [invoices]);

  const shouldPoll = pendingInvoices.length > 0
    || (orderId !== null
      && order?.status !== OrderStatus.PAID && order?.status !== OrderStatus.FAIL
    );

  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!shouldPoll) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      setLoading(false);
      return undefined;
    }

    const fetchData = async () => {
      setLoading(true);
      if (orderId) {
        await dispatch(fromOrder.doFetchOrderById(Number(orderId)));
      } else {
        await dispatch(
          fromInvoices.doGetInvoiceByInvoiceNumbers({
            invoiceNumbers: pendingInvoices,
          }),
        );
      }
      setLoading(false);

      // Schedule next poll
      timeoutRef.current = setTimeout(() => {
        fetchData().catch();
      }, 4000);
    };

    // Start polling
    timeoutRef.current = setTimeout(() => {
      fetchData().catch();
    }, 4000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, [order?.status, pendingInvoices, dispatch]);

  useEffect(() => {
    if (errors.length === 0 || !errors[0]) {
      return;
    }

    const { message } = errors[0];
    switch (message) {
      case GET_INVOICE_RECURLY_SUBSCRIPTION_NOT_EXISTED:
        enqueueSnackbar(
          <FormattedMessage id="invoice_recurly_subscription_not_existed" />,
          {
            variant: 'error',
            preventDuplicate: true,
            key: intl.formatMessage({ id: 'invoice_recurly_subscription_not_existed' }),
            autoHideDuration: 2000,
            resumeHideDuration: 0,
          },
        );
        dispatch(fromInvoices.doResetErrors());
        setIsForbiddenError(true);
        break;
      case GET_INVOICE_CANNOT_READ_RECURLY_SUBSCRIPTION:
        enqueueSnackbar(
          <FormattedMessage id="invoice_cannot_read_recurly_subscription" />,
          {
            variant: 'error',
            preventDuplicate: true,
            key: intl.formatMessage({ id: 'invoice_cannot_read_recurly_subscription' }),
            autoHideDuration: 2000,
            resumeHideDuration: 0,
          },
        );
        dispatch(fromInvoices.doResetErrors());
        setIsForbiddenError(true);
        break;
      default:
        break;
    }
  }, [errors]);

  // if error code is permission errors -> 403 page but with the sidebars and stuff.
  if (isForbiddenError) {
    return <Forbidden />;
  }

  return (
    <Box id="payment-status" className={classes.root}>
      <Panel
        id="payment-status-panel"
        sx={{
          height: 'auto',
          mt: 5,
        }}
      >
        <Grid
          id="payment-status-container"
          container
          textAlign="center"
          justifyContent="center"
          alignItems="center"
          className={classes.statusContainer}
        >
          <Grid item xs={12}>
            <Typography variant="h2" className={classes.statusTitle}>
              <FormattedMessage id="payment_status.processed_status" />
            </Typography>
            <Typography variant="h3" className={classes.statusDescription}>
              <FormattedMessage
                id="payment_status.processed_description"
                values={{
                  br: <br />,
                }}
              />
            </Typography>
          </Grid>
          {!shouldPoll && (
            <Grid item xs={12}>
              <div>
                <ButtonToSubscriptionManagement />
                <ButtonToLicenseManagement
                  title={isTrue ? 'go.to.true.platform' : undefined}
                />
              </div>
            </Grid>
          )}
          {(failedInvoices.length > 0 || (order && order?.status === OrderStatus.FAIL)) && (
            <Grid item xs={12}>
              <div>
                <ButtonToOrderConfirmation />
              </div>
            </Grid>
          )}
        </Grid>
      </Panel>
      <Panel
        id="order-summary"
        headerLabel={(
          <FormattedMessage id="payment_success.order_summary.title" />
        )}
        headerRightItem={order && orderId ? (
          <Box textAlign="right">
            <Typography fontSize={13} fontWeight={600} color="#233145">
              {intl.formatMessage(
                { id: 'payment_success.order_summary.order_number' },
                { orderId: order.id },
              )}
            </Typography>
            <Typography fontSize={12} color="#6B7280">
              {intl.formatMessage(
                { id: 'payment_success.order_summary.order_placed' },
                {
                  date: intl.formatDate(new Date(order.orderDate), {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric',
                  }),
                },
              )}
            </Typography>
          </Box>
        ) : undefined}
        sx={{
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          mt: 3,
        }}
      >
        <Box padding={3}>
          {orderId
            ? (
              <OrderTable order={order} />
            )
            : (
              <InvoiceTable
                singleProductInvoices={singleProductInvoices}
              />
            )}
        </Box>
      </Panel>
      {loading && <LoadingProgress fullscreen />}
    </Box>
  );
};

export default PaymentStatus;
